import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  List,
  ListItem,
  Pagination,
  Button,
  Select,
  InputLabel,
  FormControl,
  MenuItem,
  Stack,
  Chip,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { Bookmark, BookmarkPlus, Link2 } from "lucide-react";
import CaseLawPreviewDialog from "../dialogs/CaseLawPreviewDialog";

import { fetchMaxims, searchResults } from "../actions/searchActions";
import {
  Lightbulb,
  CheckCircle,
  XCircle,
  Building,
  Scale,
  Gavel,
  Users,
  BookOpen,
  Quote,
  Clock,
  Info,
  TrendingUp,
  Book,
  Quote as QuoteIcon,
} from "lucide-react";
import CaseDetailDialog from "../dialogs/CaseDetailDialog";
import CaseSummaryDialog from "../dialogs/CaseSummaryDialog";
import { showInsufficientCreditsModal } from "../actions/creditActions";
import { deductCredits } from "../utils/HelperFunction/deductCredits";
import { parseCitationNames } from "../utils/HelperFunction/textFormatting";
import { fetchBookmarkGroups } from "../store/Bookmarks/bookmarksActions";
import ReferenceCaseModal from "../dialogs/ReferenceCaseDialog";
import { CMSProvider } from "./CaseManagement/context/CaseManagmentContext";
import { toast } from "react-toastify";
import CollapsibleList from "./UI/CollapsibleList";
import MaximsListing from "./CaseSearch/MaximsListing";
import Maxims from "./UI/Maxims";
import LegalTerms from "./CaseSearch/LegalTerms";
import WordsAndPhrases from "./CaseSearch/WordsAndPhrases";
import NoResultsFound from "./UI/NoResultsFound";
import CaseSearchHeader from "./UI/CaseSearchHeader";
import LogoLoader from "./UI/LogoLoader";
import { File, FileText, Search } from "lucide-react";
import ExpandableSummary from "./UI/ExpandableSummary";
import BookmarkDialog from "../dialogs/BookmarkDialog";
import DateChip from "./UI/DateChip";
import { addCaseReferenceApi } from "../services/api/caseManagementApi";
import { removeItemFromGroupApi } from "../services/api/bookmarksApi";
import { BACKEND_ENDPOINTS } from "../services/apiEndpoints";

const SearchResults = ({
  toggleSidebar,
  searchKeyword,
  setSearchKeyword,
  currentPage,
  setCurrentPage,
  setIsNewCaseLawSearch,
  searchMode,
  updateSearchMode,
  resultsCount,
  updateResultsCount,
  activeTab,
  setActiveTab,
  setOpenCaseInsights,
}) => {
  const dispatch = useDispatch();
  const { state } = useLocation();

  const searchResultsState = useSelector((state) => state.searchResults);
  const { loading, results } = searchResultsState;

  const searchSelectedFilters = useSelector((state) => state.selectedFilters);
  const { selectedFilters } = searchSelectedFilters;

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const bookmarkGroups = useSelector((state) => state.bookmarkGroups);
  const { groups } = bookmarkGroups;

  const [openPreview, setOpenPreview] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [openBookmarkDialog, setOpenBookmarkDialog] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState("");

  const [bookmarkedCaseIds, setBookmarkedCaseIds] = useState(new Set());
  const [openSummary, setOpenSummary] = useState(false);
  const [isMount, setIsMount] = useState(false);

  const [openRelevantStatutes, setOpenRelevantStatutes] = useState({});
  const [openMaxims, setOpenMaxims] = useState({});
  const [openTerms, setOpenTerms] = useState({});
  const [openPhrases, setOpenPhrases] = useState({});
  const [countLabel, setCountLabel] = useState("Total Cases");
  const [loadingMaxims, setLoadingMaxims] = useState(false);
  const [listings, setListings] = useState({});
  const [listingsCurrentPage, setListingsCurrentPage] = useState(1);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearchClicked, setIsSearchClicked] = useState(false);
  const [openJudgement, setOpenJudgement] = useState({});
  const [openJudgementText, setOpenJudgementText] = useState({});

  const isInitialSearchDone = useRef(false);
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const isLaptopView = useMediaQuery((theme) => theme.breakpoints.down("xl"));
  const credits = useSelector((state) => state.credit.credits);
  const userId = useSelector((state) => state.credit.userId);

  // for adding reference case below are there states, which would be removed in future
  const [open, setOpen] = useState(false);
  const [selectedReferenceCase, setSelectedReferenceCase] = useState("");
  const [isAddCaseLoading, setIsAddCaseLoading] = useState(false);
  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    setSelectedReferenceCase("");
  };

  const handleAddCaseToCaseManagement = async () => {
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal()); // Trigger modal if credits are insufficient
      return;
    }

    if (!selectedReferenceCase) {
      console.error("No reference case selected.");
      return;
    }

    setIsAddCaseLoading(true);
    try {
      const url = `${BACKEND_ENDPOINTS.BASE_URL}${BACKEND_ENDPOINTS.CASE_MANAGEMENT_CASES}/${selectedReferenceCase}/references`;

      const response = await addCaseReferenceApi(
        selectedReferenceCase,
        selectedCase?.Bookmark_ID,
      );
      const data = response.data;

      if (response.status !== 200) {
        toast.error(data.message || "Failed to add case reference.");
      }

      handleClose();
      // Deduct credits using the helper
      const deductionSuccess = deductCredits(
        {
          userId,
          creditsToDeduct: 1,
          event: "Fetch case summary on Case Search",
        },
        dispatch,
      );

      if (!deductionSuccess) {
        console.warn(
          "Failed to deduct credits. Ensure retry or user notification.",
        );
      }
    } catch (error) {
      console.error("Error adding case:", error);
    } finally {
      setIsAddCaseLoading(false);
      handleClose();
    }
  };

  const handleCloseSummary = () => {
    setOpenSummary(false);
  };

  const handleSearchModeChange = (value) => {
    updateSearchMode(value);
  };

  const handlePreviewClick = (caseItem) => {
    setSelectedCase(caseItem);
    setOpenPreview(true);
  };

  const handleSummaryClick = async (caseItem) => {
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal());
      return;
    }
    setSelectedCase(caseItem);
    setOpenSummary(true);
    // Deduct credits using the helper
    const deductionSuccess = deductCredits(
      {
        userId,
        creditsToDeduct: 1,
        event: "Fetch case summary on Case Search",
      },
      dispatch,
    );

    if (!deductionSuccess) {
      console.warn(
        "Failed to deduct credits. Ensure retry or user notification.",
      );
    }
  };

  const copyCaseLink = (caseItem) => {
    const link = `${window.location.origin}/app/searchResults/${caseItem.Bookmark_ID}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copied to clipboard");
  };

  const handleBookmarkClick = async (caseItem) => {
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal());
      return;
    }

    setSelectedCase(caseItem);
    const defaultGroup = groups.find(
      (group) => group.groupName === "Bookmarks",
    );

    setSelectedGroup(
      defaultGroup === undefined ? groups[0]._id : defaultGroup._id,
    );
    setOpenBookmarkDialog(true);
    // Deduct credits using the helper
    const deductionSuccess = deductCredits(
      { userId, creditsToDeduct: 1, event: "Bookmark on case Search" },
      dispatch,
    );

    if (!deductionSuccess) {
      console.warn(
        "Failed to deduct credits. Ensure retry or user notification.",
      );
    }
  };

  const handleCloseBookmarkDialog = () => {
    setOpenBookmarkDialog(false);
  };

  const findGroupIdByCaseId = (documents, caseId) => {
    // Find the document where the caseId exists in the 'cases' array
    const group = documents.find((doc) =>
      doc.cases.some((caseItem) => caseItem.id === caseId),
    );

    // Return the _id of the group or null if not found
    return group ? group._id : null;
  };

  const handleUnBookmark = async (caseId) => {
    try {
      let creditsToDeduct = 1;
      if (credits <= 0 || credits <= creditsToDeduct) {
        dispatch(showInsufficientCreditsModal());
        return;
      }

      const groupId = findGroupIdByCaseId(groups, caseId);
      const response = await removeItemFromGroupApi(
        userInfo._id,
        groupId,
        caseId,
        null,
      );

      if (response.status === 200) {
        toast.success("Bookmark removed");
        // Update the state by removing the case ID from bookmarked case IDs
        setBookmarkedCaseIds((prevIds) => {
          const updatedIds = new Set(prevIds);
          updatedIds.delete(caseId);
          return updatedIds;
        });
        // Deduct credits using the helper
        const deductionSuccess = deductCredits(
          { userId, creditsToDeduct: 1, event: "Un Bookmark on Case Search" },
          dispatch,
        );

        if (!deductionSuccess) {
          console.warn(
            "Failed to deduct credits. Ensure retry or user notification.",
          );
        }
      } else {
        console.error("Error deleting bookmarked case");
      }
    } catch (error) {
      console.error("Error deleting bookmarked case:", error);
    }
  };

  const handlePageChange = async (event, value) => {
    setCurrentPage(value);
    dispatch({
      type: "SEARCH_FILTER_PAGINATION_SET",
      payload: { pageNumber: value, resultsCount },
    });
  };

  const formatCourtName = (courtName) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(courtName, "text/html");

    const formatTextContent = (name) => {
      return name
        .toLowerCase()
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    };

    const walkNodes = (node) => {
      node.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          child.nodeValue = formatTextContent(child.nodeValue);
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          walkNodes(child);
        }
      });
    };

    walkNodes(doc.body);

    return doc.body.innerHTML;
  };

  const formatCourtNameWithoutTags = (courtName) => {
    if (!courtName) return "";

    return courtName
      .toLowerCase()
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const formatCaseTitle = (title) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(title, "text/html");

    const formatTextContent = (text) => {
      return text
        .split("Honorable")[0]
        .toLowerCase()
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(" ");
    };

    const walkNodes = (node) => {
      node.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          child.nodeValue = formatTextContent(child.nodeValue);
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          walkNodes(child);
        }
      });
    };

    walkNodes(doc.body);

    return doc.body.innerHTML;
  };

  const getHighlightedSentences = (descriptionArray) => {
    if (!Array.isArray(descriptionArray) || descriptionArray.length === 0)
      return "";

    let sentences = [];

    // Split the string into sentences using delimiters like ., ?, !, etc.
    const splitSentences = descriptionArray.join(" ").split(/(?<=[.!?,])\s+/); // Split on punctuation followed by space
    sentences = sentences.concat(splitSentences); // Merge into one array of sentences

    // Prioritize sentences that contain <span> tags (highlighted parts)
    const highlightedSentences = sentences.filter((sentence) =>
      /<span/.test(sentence),
    );
    const unhighlightedSentences = sentences.filter(
      (sentence) => !/<span/.test(sentence),
    );

    // Combine the highlighted sentences first, followed by the unhighlighted ones
    const resultSentences = [
      ...highlightedSentences,
      ...unhighlightedSentences,
    ];

    // Join the resulting sentences back together into a single string
    return resultSentences.join(" ").trim();
  };

  const hanldeFetchMaxims = async (id) => {
    if (id === listings.Bookmark_ID) return;
    setLoadingMaxims(true);
    try {
      const data = await fetchMaxims(id);
      setListings(data);
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Failed to fetch maxims:", error);
      }
    } finally {
      setLoadingMaxims(false);
    }
  };

  const totalResults = results?.count || 0;

  useEffect(() => {
    if (state?.searchQuery) {
      setSearchKeyword(state.searchQuery);
    }
  }, [state?.searchQuery]);

  useEffect(() => {
    const isFiltersEmpty = Object.values(
      selectedFilters?.updatedSelectedValues || {},
    ).every((v) => !v);

    if (!hasSearched && isFiltersEmpty) {
      setCountLabel("Total Cases");
    } else {
      setCountLabel("Cases Found");
    }
  }, [searchKeyword, selectedFilters]);

  useEffect(() => {
    if (!isInitialSearchDone.current) {
      dispatch(
        searchResults(
          searchKeyword || state?.searchQuery || "",
          "",
          (currentPage - 1) * resultsCount,
          resultsCount,
          searchMode,
        ),
      );
      isInitialSearchDone.current = true;
      state?.searchQuery && setIsSearchClicked(true);
      setIsMount(true);
    }
  }, [dispatch, searchKeyword, currentPage, resultsCount, searchMode]);

  useEffect(() => {
    if (!selectedFilters && isMount) {
      dispatch(
        searchResults(
          searchKeyword,
          "",
          (currentPage - 1) * resultsCount,
          resultsCount,
          searchMode,
        ),
      );
    }
  }, [currentPage, dispatch, resultsCount]);

  useEffect(() => {
    // Fetch bookmark groups when the component mounts or when userInfo changes
    if (userInfo._id) {
      dispatch(fetchBookmarkGroups(userInfo._id));
    }
    return () => {
      setSearchKeyword("");
      if (isMobileView) {
        dispatch({ type: "SEARCH_SELECTED_FILTERS_RESET" });
      }
    };
  }, []);

  useEffect(() => {
    if (groups) {
      // Extract all case IDs from the cases array in each group document
      const allCaseIds = groups?.flatMap((group) =>
        group.cases.map((caseItem) => caseItem.id),
      );

      setBookmarkedCaseIds(new Set(allCaseIds)); // Store IDs in a Set for fast lookup
      const defaultGroup = groups?.find(
        (group) => group.groupName === "Bookmarks",
      );
      setSelectedGroup(defaultGroup?._id);
    }
  }, [groups]);

  return (
    <>
      <Helmet>
        <title>LawGPT - Case Search</title>
      </Helmet>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          boxSizing: "border-box",
          overflow: "hidden",
          padding: {
            xs: "8px 8px 0 8px",
            lg: "12px 12px 0 4px",
          },
          backgroundColor: "#f5f5f5",
        }}
      >
        <CaseSearchHeader
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          toggleSidebar={toggleSidebar}
          resultsCount={resultsCount}
          searchMode={searchMode}
          updateSearchMode={handleSearchModeChange}
          searchKeyword={searchKeyword}
          setSearchKeyword={setSearchKeyword}
          isSearchClicked={isSearchClicked}
          setIsSearchClicked={setIsSearchClicked}
          updateCurrentPage={setCurrentPage}
          setIsNewCaseLawSearch={setIsNewCaseLawSearch}
          setListingsCurrentPage={setListingsCurrentPage}
          hasSearched={hasSearched}
          setHasSearched={setHasSearched}
          openJudgement={openJudgement}
          setOpenJudgement={setOpenJudgement}
          openJudgementText={openJudgementText}
          setOpenJudgementText={setOpenJudgementText}
        />

        {activeTab === 0 ? (
          <>
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 3,
                }}
              >
                <LogoLoader size={isMobileView ? "sm" : "md"} duration="1s" />
              </Box>
            ) : (
              <React.Fragment>
                {results?.count === 0 ? (
                  <NoResultsFound />
                ) : (
                  <React.Fragment>
                    {/* Filter chips for mobile screens */}
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      gap={1}
                      sx={{
                        display: { xs: "flex", lg: "none" },
                        justifyContent: "start",
                        width: "100%",
                        mb: "8px",
                      }}
                    >
                      {selectedFilters?.updatedSelectedValues &&
                        Object.entries(
                          selectedFilters?.updatedSelectedValues,
                        )?.map(([key, value]) => (
                          <Chip
                            size="small"
                            key={key}
                            label={`${key}: ${
                              key === "Year"
                                ? value?.startYear && value?.endYear
                                  ? `${value.startYear} - ${value.endYear}`
                                  : value || "N/A"
                                : ["Court", "Judge", "Lawyer"].includes(key) &&
                                    value?.formatted
                                  ? value.formatted
                                  : value || "N/A"
                            }`}
                            aria-label={`Filter applied: ${key}: ${
                              key === "Year"
                                ? `${value?.startYear || "N/A"}-${
                                    value?.endYear || "N/A"
                                  }`
                                : value
                            }`}
                            sx={{
                              backgroundColor: "#e3f2fd",
                              color: "#1976d2",
                              fontSize: { xs: "12px", sm: "14px" },
                              fontWeight: "500",
                            }}
                          />
                        ))}
                    </Stack>
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          backgroundColor: "rgb(255, 255, 255)",
                          borderTopLeftRadius: "8px",
                          borderTopRightRadius: "8px",
                          mt: "8px",
                          p: "16px 16px 8px 16px",
                        }}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            backgroundColor: "rgba(15, 72, 128, 0.08)",
                            borderRadius: "6px",
                            boxSizing: "border-box",
                            padding: "4px 12px",
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": {
                              backgroundColor: "rgba(15, 72, 128, 0.12)",
                              transform: "translateY(-1px)",
                              boxShadow: "0 2px 4px rgba(15, 72, 128, 0.1)",
                            },
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontSize: { xs: "0.75rem", sm: "0.875rem" },
                              fontWeight: 600,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <span
                              style={{
                                color: "#0F4880",
                                fontSize: { xs: "0.875rem", sm: "1rem" },
                                fontWeight: 700,
                              }}
                            >
                              {totalResults.toLocaleString()}
                            </span>
                            <span
                              style={{
                                color: "rgba(15, 72, 128, 0.7)",
                                fontWeight: 500,
                              }}
                            >
                              {countLabel}
                            </span>
                          </Typography>
                          <Tooltip title="Total number of cases found in the database">
                            <Info
                              size={16}
                              style={{
                                color: "rgba(15, 72, 128, 0.6)",
                                transition: "all 0.2s ease",
                                cursor: "pointer",
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.color = "#0F4880";
                                e.target.style.transform = "scale(1.1)";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.color = "rgba(15, 72, 128, 0.6)";
                                e.target.style.transform = "scale(1)";
                              }}
                            />
                          </Tooltip>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <InputLabel
                            sx={{
                              display: isMobileView ? "none" : "block",
                              marginRight: "4px",
                              fontSize: "13px",
                              color: "#555",
                              fontWeight: "500",
                            }}
                            htmlFor="resultsCount"
                          >
                            Show:
                          </InputLabel>
                          <FormControl
                            variant="outlined"
                            sx={{
                              width: 100,
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "6px",
                                height: "30px",
                                padding: "8px",
                              },
                              "& fieldset": {
                                borderColor: "#ddd",
                                borderWidth: "1px",
                              },
                              "& .MuiSelect-icon": {
                                right: "4px",
                              },
                              "& .MuiSelect-select": {
                                padding: "0px",
                                display: "flex",
                                alignItems: "center",
                              },
                            }}
                          >
                            <Select
                              id="resultsCount"
                              value={resultsCount}
                              onChange={async (e) => {
                                let creditsToDeduct = 1;
                                if (
                                  credits <= 0 ||
                                  credits <= creditsToDeduct
                                ) {
                                  dispatch(showInsufficientCreditsModal());
                                  return;
                                }
                                updateResultsCount(e.target.value);
                                setCurrentPage(1);
                                // Deduct credits using the helper
                                const deductionSuccess = deductCredits(
                                  {
                                    userId,
                                    creditsToDeduct: 1,
                                    event: "Change Page Size on Case Search",
                                  },
                                  dispatch,
                                );

                                if (!deductionSuccess) {
                                  console.warn(
                                    "Failed to deduct credits. Ensure retry or user notification.",
                                  );
                                }
                              }}
                              sx={{
                                fontSize: { xs: "12px", md: "13px" },
                                height: "30px",
                                bgcolor: "#fff",
                              }}
                            >
                              <MenuItem
                                value={10}
                                sx={{ fontSize: { xs: "12px", md: "14px" } }}
                              >
                                10 results
                              </MenuItem>
                              <MenuItem
                                value={20}
                                sx={{ fontSize: { xs: "12px", md: "14px" } }}
                              >
                                20 results
                              </MenuItem>
                              <MenuItem
                                value={50}
                                sx={{ fontSize: { xs: "12px", md: "14px" } }}
                              >
                                50 results
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </Box>
                      <List
                        sx={{
                          flexGrow: 1,
                          p: 0,
                          backgroundColor: "rgb(255, 255, 255)",
                          borderBottomLeftRadius: "8px",
                          borderBottomRightRadius: "8px",
                          overflowY: "auto",
                          overflowX: "hidden",
                          scrollBehavior: "smooth",
                          "::-webkit-scrollbar": {
                            width: "5px",
                          },
                          "::-webkit-scrollbar-track": {
                            background: "#fff",
                          },
                          "::-webkit-scrollbar-thumb": {
                            background: "#bbb",
                            borderRadius: "6px",
                          },
                        }}
                      >
                        {results?.value &&
                          results?.value?.map((caseItem) => (
                            <ListItem
                              key={caseItem.doc_id}
                              sx={{
                                alignItems: "flex-start",
                                width: "100%",
                                boxSizing: "border-box",
                                overflow: "hidden",
                              }}
                            >
                              <Box
                                sx={{
                                  p: 2,
                                  width: "100%",
                                  border: "1px solid rgba(0, 0, 0, 0.08)",
                                  maxWidth: "100%",
                                  boxSizing: "border-box",
                                  overflow: "hidden",
                                  borderRadius: "12px",
                                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
                                  backgroundColor: "#fff",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
                                    borderColor: "rgba(0, 0, 0, 0.12)",
                                  },
                                }}
                              >
                                {/* Row of Card - Title, Result, Judgement Date all in one row */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    width: "100%",
                                  }}
                                >
                                  {/* Title - Left side */}
                                  <Box
                                    sx={{
                                      flex: "1 1 auto",
                                      pr: 2,
                                      overflow: "hidden",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: isMobileView
                                          ? "14px"
                                          : "16px",
                                        fontWeight: 600,
                                        color: "#1E293B",
                                        lineHeight: 1.5,
                                        cursor: "pointer",
                                        fontFamily: "Inter, sans-serif",
                                        "&:hover": { color: "#2563EB" },
                                        textOverflow: "ellipsis",
                                        overflow: "hidden",
                                        transition: "color 0.2s ease",
                                      }}
                                      onClick={() => {
                                        dispatch({
                                          type: "CASE_DETAIL_SUCCESS",
                                          payload: caseItem,
                                        });
                                        setOpenCaseInsights(true);
                                      }}
                                      dangerouslySetInnerHTML={{
                                        __html: formatCaseTitle(
                                          caseItem["@search.highlights"]
                                            ?.Title?.[0] || caseItem?.Title,
                                        ),
                                      }}
                                    />
                                  </Box>

                                  {/* Result and Judgement Date - Right side */}
                                  <Box
                                    sx={{
                                      display: isMobileView ? "none" : "flex",
                                      alignItems: "center",
                                      justifyContent: "flex-end",
                                      flexWrap: "wrap",
                                      gap: 0.5,
                                      flex: "0 1 auto",
                                      minWidth: 0,
                                      flexShrink: 1,
                                    }}
                                  >
                                    {/* Appeal Result - First on the right */}
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.75,
                                        backgroundColor:
                                          caseItem.Result?.toLowerCase().includes(
                                            "accepted",
                                          )
                                            ? "rgba(16, 185, 129, 0.05)"
                                            : caseItem.Result?.toLowerCase().includes(
                                                  "dismissed",
                                                )
                                              ? "rgba(239, 68, 68, 0.05)"
                                              : "rgba(20, 184, 166, 0.05)", // Default: indigo/blue color for pending cases
                                        padding: "4px 12px",
                                        borderRadius: "20px",
                                        border: "0.5px solid",
                                        borderColor:
                                          caseItem.Result?.toLowerCase().includes(
                                            "accepted",
                                          )
                                            ? "rgba(16, 185, 129, 0.2)"
                                            : caseItem.Result?.toLowerCase().includes(
                                                  "dismissed",
                                                )
                                              ? "rgba(239, 68, 68, 0.2)"
                                              : "rgba(20, 184, 166, 0.2)", // Default border color
                                        transition: "all 0.2s ease",
                                        boxShadow:
                                          "0 1px 2px rgba(0, 0, 0, 0.05)",
                                        whiteSpace: "nowrap",
                                        "&:hover": {
                                          backgroundColor:
                                            caseItem.Result?.toLowerCase().includes(
                                              "accepted",
                                            )
                                              ? "rgba(16, 185, 129, 0.1)"
                                              : caseItem.Result?.toLowerCase().includes(
                                                    "dismissed",
                                                  )
                                                ? "rgba(239, 68, 68, 0.1)"
                                                : "rgba(20, 184, 166, 0.1)", // Default hover color
                                          transform: "translateY(-1px)",
                                          boxShadow:
                                            "0 2px 4px rgba(0, 0, 0, 0.1)",
                                        },
                                      }}
                                    >
                                      {caseItem.Result?.toLowerCase().includes(
                                        "accepted",
                                      ) ? (
                                        <CheckCircle
                                          size={12}
                                          color="#059669"
                                        />
                                      ) : caseItem.Result?.toLowerCase().includes(
                                          "dismissed",
                                        ) ? (
                                        <XCircle size={12} color="#DC2626" />
                                      ) : (
                                        <Clock size={12} color="#0D9488" />
                                      )}
                                      <Typography
                                        sx={{
                                          fontSize: "0.75rem",
                                          fontWeight: 600,
                                          color:
                                            caseItem.Result?.toLowerCase().includes(
                                              "accepted",
                                            )
                                              ? "#059669" // Green
                                              : caseItem.Result?.toLowerCase().includes(
                                                    "dismissed",
                                                  )
                                                ? "#DC2626" // Red
                                                : "#0D9488", // Indigo/blue for pending
                                        }}
                                      >
                                        {caseItem.Result?.toLowerCase().includes(
                                          "accepted",
                                        )
                                          ? "Appeal Accepted"
                                          : caseItem.Result?.toLowerCase().includes(
                                                "dismissed",
                                              )
                                            ? "Appeal Dismissed"
                                            : "Under Review"}{" "}
                                        {/* Default text */}
                                      </Typography>
                                    </Box>

                                    {/* Judgement Date - Second on the right */}
                                    <DateChip
                                      date={caseItem?.Date_Of_Judgement}
                                      label="Judgement Date"
                                    />
                                  </Box>
                                </Box>

                                {/* Info Section - Chips Row */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0.75,
                                    mb: 1.5,
                                  }}
                                >
                                  {/* First Row - Court and Citations */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 0.75,
                                    }}
                                  >
                                    {/* Court */}
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      <Building size={12} color="#059669" />
                                      <Chip
                                        label={
                                          caseItem["@search.highlights"]
                                            ?.Court ? (
                                            <span
                                              dangerouslySetInnerHTML={{
                                                __html: formatCourtName(
                                                  caseItem["@search.highlights"]
                                                    ?.Court || "",
                                                ),
                                              }}
                                            />
                                          ) : (
                                            formatCourtNameWithoutTags(
                                              caseItem.Court || "",
                                            )
                                          )
                                        }
                                        size="small"
                                        sx={{
                                          height: "20px",
                                          fontSize: "12px",
                                          backgroundColor: "#F8FAFC",
                                          color: "#475569",
                                          border: "1px solid #E2E8F0",
                                          borderRadius: "8px",
                                          fontFamily: "Inter, sans-serif",
                                          fontWeight: 500,
                                          "&:hover": {
                                            backgroundColor: "#F1F5F9",
                                            borderColor: "#CBD5E1",
                                          },
                                        }}
                                      />
                                    </Box>

                                    {/* Citations */}
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                        overflow: "hidden",
                                      }}
                                    >
                                      <Scale size={12} color="#DC2626" />
                                      {(() => {
                                        const highlightedCitation =
                                          caseItem["@search.highlights"]
                                            ?.Citation_Name;
                                        const citations = parseCitationNames(
                                          highlightedCitation ||
                                            caseItem?.Citation_Name,
                                        );

                                        return citations.map(
                                          (citation, index) => (
                                            <Chip
                                              key={index}
                                              label={
                                                highlightedCitation ? (
                                                  <span
                                                    dangerouslySetInnerHTML={{
                                                      __html: citation,
                                                    }}
                                                  />
                                                ) : (
                                                  citation
                                                )
                                              }
                                              size="small"
                                              sx={{
                                                height: "18px",
                                                fontSize: "0.65rem",
                                                backgroundColor: "#FFFFFF",
                                                color: "#003D78",
                                                border:
                                                  "0.5px solid rgba(0, 61, 120, 0.15)",
                                                boxShadow:
                                                  "0 1px 2px rgba(0, 61, 120, 0.05)",
                                                "&:hover": {
                                                  backgroundColor:
                                                    "rgba(0, 61, 120, 0.02)",
                                                  boxShadow:
                                                    "0 2px 4px rgba(0, 61, 120, 0.08)",
                                                },
                                              }}
                                            />
                                          ),
                                        );
                                      })()}
                                    </Box>
                                  </Box>

                                  {/* Second Row - Judges and Lawyers */}
                                  <Box
                                    sx={{
                                      display: isMobileView ? "none" : "flex",
                                      flexWrap: "wrap",
                                      gap: 0.75,
                                    }}
                                  >
                                    {/* Judges */}
                                    {caseItem?.Judge_Name.length > 0 && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          flexWrap: "wrap",
                                          gap: 0.5,
                                          overflow: "hidden",
                                        }}
                                      >
                                        <Gavel size={12} color="#7C3AED" />
                                        {caseItem["@search.highlights"]
                                          ?.Judge_Name
                                          ? caseItem[
                                              "@search.highlights"
                                            ].Judge_Name.map((judge, index) => (
                                              <Chip
                                                key={index}
                                                label={
                                                  <span
                                                    dangerouslySetInnerHTML={{
                                                      __html: judge,
                                                    }}
                                                  />
                                                }
                                                size="small"
                                                sx={{
                                                  height: "18px",
                                                  fontSize: "0.65rem",
                                                  backgroundColor: "#FFFFFF",
                                                  color: "#003D78",
                                                  border:
                                                    "0.5px solid rgba(0, 61, 120, 0.15)",
                                                  boxShadow:
                                                    "0 1px 2px rgba(0, 61, 120, 0.05)",
                                                  "&:hover": {
                                                    backgroundColor:
                                                      "rgba(0, 61, 120, 0.02)",
                                                    boxShadow:
                                                      "0 2px 4px rgba(0, 61, 120, 0.08)",
                                                  },
                                                }}
                                              />
                                            ))
                                          : caseItem?.Judge_Name.map(
                                              (judge, index) => (
                                                <Chip
                                                  key={index}
                                                  label={judge}
                                                  size="small"
                                                  sx={{
                                                    height: "18px",
                                                    fontSize: "0.65rem",
                                                    backgroundColor: "#FFFFFF",
                                                    color: "#003D78",
                                                    border:
                                                      "0.5px solid rgba(0, 61, 120, 0.15)",
                                                    boxShadow:
                                                      "0 1px 2px rgba(0, 61, 120, 0.05)",
                                                    "&:hover": {
                                                      backgroundColor:
                                                        "rgba(0, 61, 120, 0.02)",
                                                      boxShadow:
                                                        "0 2px 4px rgba(0, 61, 120, 0.08)",
                                                    },
                                                  }}
                                                />
                                              ),
                                            )}
                                      </Box>
                                    )}

                                    {/* Lawyers */}
                                    {/* {caseItem?.Lawyer_Name.length > 0 && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                        }}
                                      >
                                        <Users size={12} color="#F59E0B" />
                                        {caseItem["@search.highlights"]
                                          ?.Lawyer_Name
                                          ? caseItem[
                                              "@search.highlights"
                                            ]?.Lawyer_Name.map(
                                              (lawyer, index) => (
                                                <Chip
                                                  key={index}
                                                  label={
                                                    <span
                                                      dangerouslySetInnerHTML={{
                                                        __html: lawyer,
                                                      }}
                                                    />
                                                  }
                                                  size="small"
                                                  sx={{
                                                    height: "18px",
                                                    fontSize: "0.65rem",
                                                    backgroundColor: "#FFFFFF",
                                                    color: "#003D78",
                                                    border:
                                                      "0.5px solid rgba(0, 61, 120, 0.15)",
                                                    boxShadow:
                                                      "0 1px 2px rgba(0, 61, 120, 0.05)",
                                                    "&:hover": {
                                                      backgroundColor:
                                                        "rgba(0, 61, 120, 0.02)",
                                                      boxShadow:
                                                        "0 2px 4px rgba(0, 61, 120, 0.08)",
                                                    },
                                                  }}
                                                />
                                              ),
                                            )
                                          : caseItem?.Lawyer_Name.map(
                                              (lawyer, index) => (
                                                <Chip
                                                  key={index}
                                                  label={lawyer}
                                                  size="small"
                                                  sx={{
                                                    height: "18px",
                                                    fontSize: "0.65rem",
                                                    backgroundColor: "#FFFFFF",
                                                    color: "#003D78",
                                                    border:
                                                      "0.5px solid rgba(0, 61, 120, 0.15)",
                                                    boxShadow:
                                                      "0 1px 2px rgba(0, 61, 120, 0.05)",
                                                    "&:hover": {
                                                      backgroundColor:
                                                        "rgba(0, 61, 120, 0.02)",
                                                      boxShadow:
                                                        "0 2px 4px rgba(0, 61, 120, 0.08)",
                                                    },
                                                  }}
                                                />
                                              ),
                                            )}
                                      </Box>
                                    )} */}
                                  </Box>
                                </Box>
                                <ExpandableSummary
                                  summary={caseItem?.Short_Summary}
                                  highlights={caseItem["@search.highlights"]}
                                  isMobileView={isMobileView}
                                />

                                {/* Bottom Section */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                    gap: 1,
                                    overflow: "hidden",
                                  }}
                                >
                                  {/* Action Buttons */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: isMobileView
                                        ? "space-between"
                                        : "initial",
                                      flexWrap: "wrap",
                                      gap: 0.5,
                                      mt: 1,
                                    }}
                                  >
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={() => {
                                        handlePreviewClick(caseItem);
                                      }}
                                      startIcon={<Search size={14} />}
                                      sx={{
                                        fontSize: "12px",
                                        color: "#2563EB",
                                        backgroundColor: "#EFF6FF",
                                        border: "1px solid #DBEAFE",
                                        borderRadius: "8px",
                                        padding: "6px 12px",
                                        minWidth: "auto",
                                        height: "28px",
                                        fontFamily: "Inter, sans-serif",
                                        fontWeight: 500,
                                        textTransform: "none",
                                        "&:hover": {
                                          backgroundColor: "#DBEAFE",
                                          borderColor: "#93C5FD",
                                        },
                                      }}
                                    >
                                      {isMobileView
                                        ? "Judgement"
                                        : "Original Judgement"}
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={async () => {
                                        let creditsToDeduct = 1;
                                        if (
                                          credits <= 0 ||
                                          credits <= creditsToDeduct
                                        ) {
                                          dispatch(
                                            showInsufficientCreditsModal(),
                                          );
                                          return;
                                        }
                                        dispatch({
                                          type: "CASE_DETAIL_SUCCESS",
                                          payload: caseItem,
                                        });

                                        setOpenCaseInsights(true);
                                        const deductionSuccess = deductCredits(
                                          {
                                            userId,
                                            creditsToDeduct: 1,
                                            event:
                                              "Case Insights on Case Search",
                                          },
                                          dispatch,
                                        );

                                        if (!deductionSuccess) {
                                          console.warn(
                                            "Failed to deduct credits. Ensure retry or user notification.",
                                          );
                                        }
                                      }}
                                      startIcon={<TrendingUp size={14} />}
                                      sx={{
                                        fontSize: "12px",
                                        color: "#F59E0B",
                                        backgroundColor: "#FFFBEB",
                                        border: "1px solid #FEF3C7",
                                        borderRadius: "8px",
                                        padding: "6px 12px",
                                        minWidth: "auto",
                                        height: "28px",
                                        fontFamily: "Inter, sans-serif",
                                        fontWeight: 500,
                                        textTransform: "none",
                                        "&:hover": {
                                          backgroundColor: "#FEF3C7",
                                          borderColor: "#FCD34D",
                                        },
                                      }}
                                    >
                                      Case Insights
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={() => {
                                        handleSummaryClick(caseItem);
                                      }}
                                      startIcon={<FileText size={14} />}
                                      sx={{
                                        fontSize: "12px",
                                        color: "#7C3AED",
                                        backgroundColor: "#F3F4F6",
                                        border: "1px solid #E5E7EB",
                                        borderRadius: "8px",
                                        padding: "6px 12px",
                                        minWidth: "auto",
                                        height: "28px",
                                        fontFamily: "Inter, sans-serif",
                                        fontWeight: 500,
                                        textTransform: "none",
                                        "&:hover": {
                                          backgroundColor: "#E5E7EB",
                                          borderColor: "#D1D5DB",
                                        },
                                      }}
                                    >
                                      Summary
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={() => {
                                        copyCaseLink(caseItem);
                                      }}
                                      startIcon={<Link2 size={14} />}
                                      sx={{
                                        fontSize: "12px",
                                        color: "#475569",
                                        backgroundColor: "#F3F4F6",
                                        border: "1px solid #E5E7EB",
                                        borderRadius: "8px",
                                        padding: "6px 12px",
                                        minWidth: "auto",
                                        height: "28px",
                                        fontFamily: "Inter, sans-serif",
                                        fontWeight: 500,
                                        textTransform: "none",
                                        "&:hover": {
                                          backgroundColor: "#E5E7EB",
                                          borderColor: "#D1D5DB",
                                        },
                                      }}
                                    >
                                      Copy Link
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="text"
                                      startIcon={
                                        bookmarkedCaseIds.has(
                                          caseItem.Bookmark_ID,
                                        ) ? (
                                          <Bookmark size={14} fill="#059669" />
                                        ) : (
                                          <BookmarkPlus size={14} />
                                        )
                                      }
                                      sx={{
                                        fontSize: "12px",
                                        color: "#059669",
                                        backgroundColor: "#ECFDF5",
                                        border: "1px solid #D1FAE5",
                                        borderRadius: "8px",
                                        padding: "6px 12px",
                                        minWidth: "auto",
                                        height: "28px",
                                        fontFamily: "Inter, sans-serif",
                                        fontWeight: 500,
                                        textTransform: "none",
                                        "&:hover": {
                                          backgroundColor: "#D1FAE5",
                                          borderColor: "#A7F3D0",
                                        },
                                      }}
                                      onClick={() =>
                                        bookmarkedCaseIds.has(
                                          caseItem.Bookmark_ID,
                                        )
                                          ? handleUnBookmark(
                                              caseItem.Bookmark_ID,
                                            )
                                          : handleBookmarkClick(caseItem)
                                      }
                                    >
                                      Bookmark
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={() => {
                                        handleOpen();
                                        setSelectedCase(caseItem);
                                      }}
                                      startIcon={<File size={14} />}
                                      sx={{
                                        fontSize: "12px",
                                        color: "#2563EB",
                                        backgroundColor: "#EFF6FF",
                                        border: "1px solid #DBEAFE",
                                        borderRadius: "8px",
                                        padding: "6px 12px",
                                        minWidth: "auto",
                                        height: "28px",
                                        fontFamily: "Inter, sans-serif",
                                        fontWeight: 500,
                                        textTransform: "none",
                                        "&:hover": {
                                          backgroundColor: "#DBEAFE",
                                          borderColor: "#93C5FD",
                                        },
                                      }}
                                    >
                                      Add as reference
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={() => {
                                        setOpenRelevantStatutes((prev) => {
                                          const isAlreadyOpen =
                                            prev[caseItem?.Bookmark_ID];

                                          const newState = isAlreadyOpen
                                            ? {}
                                            : {
                                                [caseItem?.Bookmark_ID]: true,
                                              };
                                          !isAlreadyOpen && setOpenMaxims({});
                                          setOpenTerms({});
                                          setOpenPhrases({});
                                          return newState;
                                        });
                                      }}
                                      startIcon={<Scale size={14} />}
                                      sx={{
                                        fontSize: "12px",
                                        color: "#7C3AED",
                                        fontWeight: openRelevantStatutes[
                                          caseItem?.Bookmark_ID
                                        ]
                                          ? "600"
                                          : "500",
                                        backgroundColor: "#F3F4F6",
                                        border: "1px solid #E5E7EB",
                                        borderRadius: "8px",
                                        padding: "6px 12px",
                                        minWidth: "auto",
                                        height: "28px",
                                        fontFamily: "Inter, sans-serif",
                                        textTransform: "none",
                                        "&:hover": {
                                          backgroundColor: "#E5E7EB",
                                          borderColor: "#D1D5DB",
                                        },
                                      }}
                                    >
                                      Statutes
                                    </Button>
                                  </Box>

                                  {/* Expandable Sections */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Button
                                      size="small"
                                      variant="text"
                                      startIcon={<Lightbulb size={14} />}
                                      sx={{
                                        fontSize: "12px",
                                        fontWeight: openMaxims[
                                          caseItem?.Bookmark_ID
                                        ]
                                          ? "600"
                                          : "500",
                                        color: "#F59E0B",
                                        backgroundColor: "#FFFBEB",
                                        border: "1px solid #FEF3C7",
                                        borderRadius: "8px",
                                        padding: "6px 12px",
                                        minWidth: "auto",
                                        height: "28px",
                                        fontFamily: "Inter, sans-serif",
                                        textTransform: "none",
                                        "&:hover": {
                                          backgroundColor: "#FEF3C7",
                                          borderColor: "#FCD34D",
                                        },
                                      }}
                                      onClick={() => {
                                        hanldeFetchMaxims(
                                          caseItem?.Bookmark_ID,
                                        );
                                        setOpenMaxims((prev) => {
                                          const isAlreadyOpen =
                                            prev[caseItem?.Bookmark_ID];
                                          const newState = isAlreadyOpen
                                            ? {}
                                            : {
                                                [caseItem?.Bookmark_ID]: true,
                                              };
                                          !isAlreadyOpen &&
                                            setOpenRelevantStatutes({});
                                          setOpenTerms({});
                                          setOpenPhrases({});
                                          return newState;
                                        });
                                      }}
                                    >
                                      Maxims
                                    </Button>

                                    <Button
                                      size="small"
                                      variant="text"
                                      startIcon={<Book size={14} />}
                                      sx={{
                                        fontSize: "12px",
                                        fontWeight: openTerms[
                                          caseItem?.Bookmark_ID
                                        ]
                                          ? "600"
                                          : "500",
                                        color: "#2563EB",
                                        backgroundColor: "#EFF6FF",
                                        border: "1px solid #DBEAFE",
                                        borderRadius: "8px",
                                        padding: "6px 12px",
                                        minWidth: "auto",
                                        height: "28px",
                                        fontFamily: "Inter, sans-serif",
                                        textTransform: "none",
                                        "&:hover": {
                                          backgroundColor: "#DBEAFE",
                                          borderColor: "#93C5FD",
                                        },
                                      }}
                                      onClick={() => {
                                        hanldeFetchMaxims(
                                          caseItem?.Bookmark_ID,
                                        );
                                        setOpenTerms((prev) => {
                                          const isAlreadyOpen =
                                            prev[caseItem?.Bookmark_ID];
                                          const newState = isAlreadyOpen
                                            ? {}
                                            : {
                                                [caseItem?.Bookmark_ID]: true,
                                              };
                                          !isAlreadyOpen &&
                                            setOpenRelevantStatutes({});
                                          setOpenMaxims({});
                                          setOpenPhrases({});
                                          return newState;
                                        });
                                      }}
                                    >
                                      Terms
                                    </Button>

                                    <Button
                                      size="small"
                                      variant="text"
                                      startIcon={<QuoteIcon size={14} />}
                                      sx={{
                                        fontSize: "12px",
                                        fontWeight: openPhrases[
                                          caseItem?.Bookmark_ID
                                        ]
                                          ? "600"
                                          : "500",
                                        color: "#7C3AED",
                                        backgroundColor: "#F3F4F6",
                                        border: "1px solid #E5E7EB",
                                        borderRadius: "8px",
                                        padding: "6px 12px",
                                        minWidth: "auto",
                                        height: "28px",
                                        fontFamily: "Inter, sans-serif",
                                        textTransform: "none",
                                        "&:hover": {
                                          backgroundColor: "#E5E7EB",
                                          borderColor: "#D1D5DB",
                                        },
                                      }}
                                      onClick={() => {
                                        hanldeFetchMaxims(
                                          caseItem?.Bookmark_ID,
                                        );
                                        setOpenPhrases((prev) => {
                                          const isAlreadyOpen =
                                            prev[caseItem?.Bookmark_ID];
                                          const newState = isAlreadyOpen
                                            ? {}
                                            : {
                                                [caseItem?.Bookmark_ID]: true,
                                              };
                                          !isAlreadyOpen &&
                                            setOpenRelevantStatutes({});
                                          setOpenTerms({});
                                          setOpenMaxims({});
                                          return newState;
                                        });
                                      }}
                                    >
                                      {isLaptopView
                                        ? "Phrases"
                                        : "Words & Phrases"}
                                    </Button>
                                  </Box>
                                </Box>

                                <CollapsibleList
                                  isOpen={
                                    openRelevantStatutes[caseItem?.Bookmark_ID]
                                  }
                                  items={caseItem?.Statute_Collection || []}
                                  label="statutes"
                                />
                                {(openMaxims[caseItem?.Bookmark_ID] ||
                                  openTerms[caseItem?.Bookmark_ID] ||
                                  openPhrases[caseItem?.Bookmark_ID]) && (
                                  <Maxims
                                    isOpen={
                                      !!openMaxims[caseItem?.Bookmark_ID] ||
                                      !!openTerms[caseItem?.Bookmark_ID] ||
                                      !!openPhrases[caseItem?.Bookmark_ID]
                                    }
                                    loading={loadingMaxims}
                                    data={listings?.Results || {}}
                                    activeSection={
                                      !!openMaxims[caseItem?.Bookmark_ID]
                                        ? "maxims"
                                        : !!openTerms[caseItem?.Bookmark_ID]
                                          ? "terms"
                                          : "phrases"
                                    }
                                  />
                                )}
                              </Box>
                            </ListItem>
                          ))}
                      </List>
                    </>
                    <CaseLawPreviewDialog
                      open={openPreview}
                      onClose={() => {
                        setOpenPreview(false);
                      }}
                      title={
                        selectedCase
                          ? formatCaseTitle(selectedCase?.Title || "")
                          : ""
                      }
                      court={selectedCase?.Court || ""}
                      citation={selectedCase?.Citation_Name || ""}
                      judge={selectedCase?.Judge_Name || ""}
                      caseDescriptionContent={
                        selectedCase?.Case_Description_HTML || ""
                      }
                      caseDescription={selectedCase?.Case_Description || ""}
                    />
                    {openSummary && (
                      <CaseSummaryDialog
                        open={openSummary}
                        onClose={handleCloseSummary}
                        title={
                          selectedCase
                            ? formatCaseTitle(selectedCase?.Title || "")
                            : ""
                        }
                        summary={selectedCase?.Detailed_Lawcase_Summary}
                      />
                    )}

                    <BookmarkDialog
                      open={openBookmarkDialog}
                      onClose={handleCloseBookmarkDialog}
                      selectedCase={selectedCase}
                      selectedGroup={selectedGroup}
                      setSelectedGroup={setSelectedGroup}
                      setBookmarkedCaseIds={setBookmarkedCaseIds}
                    />
                    {results?.value && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Pagination
                          count={Math.ceil(totalResults / resultsCount)}
                          page={currentPage}
                          onChange={handlePageChange}
                          sx={{
                            m: "5px 0",
                            "& .MuiPaginationItem-root": {
                              fontSize: "13px",
                              minWidth: "24px",
                              height: "24px",
                              "&:hover": {
                                color: "#fff",
                                backgroundColor: "#303f9f",
                              },
                              "&.Mui-selected": {
                                color: "#fff",
                                backgroundColor: "#213165",
                                "&:hover": {
                                  backgroundColor: "#303f9f",
                                },
                              },
                            },
                          }}
                        />
                      </Box>
                    )}
                  </React.Fragment>
                )}
                {open && (
                  <CMSProvider>
                    <ReferenceCaseModal
                      open={open}
                      onClose={handleClose}
                      selectedReferenceCase={selectedReferenceCase}
                      setSelectedReferenceCase={setSelectedReferenceCase}
                      isAddCaseLoading={isAddCaseLoading}
                      handleAddCaseToCaseManagement={
                        handleAddCaseToCaseManagement
                      }
                    />
                  </CMSProvider>
                )}
              </React.Fragment>
            )}
          </>
        ) : activeTab === 1 ? (
          <MaximsListing
            searchKeyword={searchKeyword}
            currentPage={listingsCurrentPage}
            setCurrentPage={setListingsCurrentPage}
            openJudgement={openJudgement}
            setOpenJudgement={setOpenJudgement}
            openJudgementText={openJudgementText}
            setOpenJudgementText={setOpenJudgementText}
          />
        ) : activeTab === 2 ? (
          <LegalTerms
            searchKeyword={searchKeyword}
            currentPage={listingsCurrentPage}
            setCurrentPage={setListingsCurrentPage}
            openJudgement={openJudgement}
            setOpenJudgement={setOpenJudgement}
            openJudgementText={openJudgementText}
            setOpenJudgementText={setOpenJudgementText}
          />
        ) : (
          <WordsAndPhrases
            searchKeyword={searchKeyword}
            currentPage={listingsCurrentPage}
            setCurrentPage={setListingsCurrentPage}
            openJudgement={openJudgement}
            setOpenJudgement={setOpenJudgement}
            openJudgementText={openJudgementText}
            setOpenJudgementText={setOpenJudgementText}
          />
        )}
      </Box>
    </>
  );
};

export default SearchResults;
